import { createContext, useContext, onMount, createEffect } from "solid-js";
import { createStore } from "solid-js/store";
import socket from "./socket";
import { useConnection } from "./ConnectionManager";
import { useOfflineData } from "./OfflineDataManager";
import { useUserContext } from "./UserContext";

const AdminContext = createContext();

export function AdminContextProvider(props: any) {
  // Get connection status from ConnectionManager
  const [connectionStore] = useConnection();
  
  // Get offline data manager
  const offlineDataManager = useOfflineData();

  const [userStore] = useUserContext();
  
  const [adminStore, setAdminStore] = createStore({
    users: [],
    conventionStructure: { fields: [] },
    conventionStructureLoading: true,
    results: false,
    resultsFetching: false,
    cachedConventions: null,
    conventionsLastUpdated: null,
    isLoadingConventions: true
  });
  
  // Effect to handle offline/online transitions
  createEffect(() => {
    // If we go offline, load cached conventions data
    if (connectionStore.isOffline && adminStore.isLoadingConventions) {
      loadCachedConventions();
    }
  });
  
  // Load conventions data from cache
  const loadCachedConventions = async () => {
    const result = await offlineDataManager.loadConventionsData();
    if (result) {
      setAdminStore({
        cachedConventions: result.conventions,
        conventionsLastUpdated: result.lastUpdated,
        isLoadingConventions: false
      });
      console.log('Admin context loaded cached conventions data');
    } else {
      setAdminStore({ isLoadingConventions: false });
      console.log('No cached conventions data found');
    }
  };
  
  // Save conventions data to cache
  const saveConventionsToCache = async (data: any) => {
    if (!data || (Array.isArray(data) && data.length === 0)) return;
    
    try {
      await offlineDataManager.saveConventionsData(data);
      setAdminStore({ 
        cachedConventions: data,
        conventionsLastUpdated: new Date().getTime()
      });
      console.log('Admin context saved conventions data to cache');
    } catch (error) {
      console.error('Failed to save conventions data to cache:', error);
    }
  };

  createEffect(() => {
    if(userStore.isAuthenticated && userStore.isAdmin) {
      socket.emit("admin-get-convention-structure", (data) => {
        if (data && data.success) {
          setAdminStore("conventionStructure", data.structure);
          setAdminStore({ conventionStructureLoading: false });
        }
      });
    }
  });

  onMount(() => {
    console.log('Mounted AdminContext');
    
    socket.on("user-list", (users: any) => {
      setAdminStore({ users });
    });
    
    socket.on('results', (results: any) => {
      setAdminStore({ results, resultsFetching: false });
    });
    
    // Handle admin conventions data
    socket.on("admin-all-conventions", (data: any) => {
      console.log("Admin received all conventions data");
      setAdminStore({ 
        cachedConventions: data,
        isLoadingConventions: false
      });
      
      // Save admin conventions data for offline use
      saveConventionsToCache(data);
    });
  });
  
  const actions = {
    // Load all conventions (for admin users)
    loadAllConventions: () => {
      if (connectionStore.isOffline) {
        // If offline, try to load from cache
        loadCachedConventions();
      } else {
        // If online, request from server
        socket.emit("admin-get-all-conventions");
      }
    },
    
    editPromo: (id: any, fields: any) => {
      if (connectionStore.isOffline) {
        console.log('Cannot edit promo in offline mode');
        return;
      }
      socket.emit('update-promo', { id, fields });
    },
    
    deletePromo: (promo_id: any) => {
      if (connectionStore.isOffline) {
        console.log('Cannot delete promo in offline mode');
        return;
      }
      socket.emit('delete-promo', promo_id);
    },
    
    addPromo: (fields: any) => {
      if (connectionStore.isOffline) {
        console.log('Cannot add promo in offline mode');
        return;
      }
      socket.emit('add-promo', fields);
    },
    
    addPromoValidator: (promo_id: any, user_id: any) => {
      if (connectionStore.isOffline) {
        console.log('Cannot add promo validator in offline mode');
        return;
      }
      socket.emit('add-promo-validator', { promo_id, user_id });
    },
    
    deletePromoValidator: (promo_id: any, user_id: any) => {
      if (connectionStore.isOffline) {
        console.log('Cannot delete promo validator in offline mode');
        return;
      }
      socket.emit('delete-promo-validator', { promo_id, user_id });
    },
    
    editUser: (index: any) => {
      if (connectionStore.isOffline) {
        console.log('Cannot edit user in offline mode');
        return;
      }
      
      const user = adminStore.users[index];
      const fields = {
        role: user.roleInput,
      };
      
      // Include email in update if it exists
      if (user.emailInput !== undefined) {
        fields.email = user.emailInput;
      }
      
      socket.emit('update-user', { id: user.id, fields });
      setAdminStore('users', index, 'editing', false);
    },
    
    deleteUser: (user_id: any) => {
      if (connectionStore.isOffline) {
        console.log('Cannot delete user in offline mode');
        return;
      }
      socket.emit('delete-user', user_id);
      setAdminStore('users', (u: any) => u.filter((user: any) => user.id !== user_id));
    },
    
    addUser: (fields: any) => {
      if (connectionStore.isOffline) {
        console.log('Cannot add user in offline mode');
        return;
      }
      socket.emit('add-user', fields);
    },
    
    lockConvention: (conventionId: any) => {
      if (connectionStore.isOffline) {
        console.log('Cannot lock convention in offline mode');
        return;
      }
      socket.emit('admin-lock-convention', conventionId, (response: any) => {
        if (response.success) {
          console.log('Convention locked successfully');
        } else {
          console.error('Failed to lock convention:', response.error);
          alert('Failed to lock convention: ' + (response.error || 'Unknown error'));
        }
      });
    },
    
    unlockConvention: (conventionId: any) => {
      if (connectionStore.isOffline) {
        console.log('Cannot unlock convention in offline mode');
        return;
      }
      socket.emit('admin-unlock-convention', conventionId, (response: any) => {
        if (response.success) {
          console.log('Convention unlocked successfully');
        } else {
          console.error('Failed to unlock convention:', response.error);
          alert('Failed to unlock convention: ' + (response.error || 'Unknown error'));
        }
      });
    }
  }
  
  return (
    <AdminContext.Provider value={[adminStore, setAdminStore, actions]}>
      {props.children}
    </AdminContext.Provider>
  );
}

export function useAdminContext() { 
  return useContext(AdminContext); 
}
